import { Link } from "react-router-dom";
import { Lock, Globe, Sun, Moon } from "lucide-react";
import { useLang, LANGUAGES } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FlagIcon from "./FlagIcon";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { theme, toggle } = useTheme();
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  const isDark = theme === "dark";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-white/70 dark:bg-black/60 backdrop-blur-xl border-b border-black/10 dark:border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-2 text-neutral-900 dark:text-white group"
        >
          <span className="inline-flex items-center justify-center w-8 h-8 border border-black/20 dark:border-white/20 group-hover:border-[#00a844] dark:group-hover:border-[#00ff66] transition-colors">
            <Lock className="w-4 h-4" strokeWidth={2.5} />
          </span>
          <span className="font-black tracking-tighter text-lg uppercase">
            {t("brand")}
          </span>
        </Link>

        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
            title={isDark ? "Light mode" : "Dark mode"}
            className="relative h-9 w-9 inline-flex items-center justify-center border border-black/15 dark:border-white/15 hover:border-black/40 dark:hover:border-white/40 transition-colors"
          >
            <Sun
              data-testid="theme-icon-sun"
              className={`w-4 h-4 absolute transition-all ${isDark ? "opacity-0 rotate-90 scale-0" : "opacity-100 rotate-0 scale-100"}`}
            />
            <Moon
              data-testid="theme-icon-moon"
              className={`w-4 h-4 absolute transition-all ${isDark ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-0"}`}
            />
          </button>

          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger
              data-testid="lang-select"
              className="h-9 w-auto min-w-[130px] bg-transparent border border-black/15 dark:border-white/15 rounded-none font-mono text-xs uppercase tracking-widest hover:border-black/40 dark:hover:border-white/40 focus:ring-1 focus:ring-black/30 dark:focus:ring-white/30"
              aria-label={t("language")}
            >
              <span className="inline-flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-neutral-500" />
                <SelectValue>
                  <span className="inline-flex items-center gap-2">
		    <FlagIcon code={current.flag} />
                    <span>{current.code.toUpperCase()}</span>
                  </span>
                </SelectValue>
              </span>
            </SelectTrigger>
            <SelectContent
              className="bg-white dark:bg-[#0a0a0a] border border-black/15 dark:border-white/15 rounded-none font-mono"
              align="end"
            >
              {LANGUAGES.map((l) => (
                <SelectItem
                  key={l.code}
                  value={l.code}
                  data-testid={`lang-option-${l.code}`}
                  className="text-xs uppercase tracking-widest"
                >
                  <span className="inline-flex items-center gap-2">
			<FlagIcon code={l.flag} />                    
		<	span>{l.label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </header>
  );
}
